/**
 * Adesa Cleared List — the working sheet for one cleared-list run.
 *
 * Mirrors the Adesa Run List's draft -> completed lifecycle: Start New Run ->
 * upload Adesa + Tekion -> a draft is created automatically (no "Run Report"
 * button). The draft is the working sheet — Status column, the validator for
 * VIN+stock picks, and a live title-status families filter that re-buckets
 * Cleared vs Not Cleared. Mark Completed locks it (Reopen undoes). The page
 * auto-resumes the latest draft and the switcher lists drafts + completed.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import ActiveVendorFilesPanel from "../../../components/wholesale/ActiveVendorFilesPanel"
import ClearedCandidateModal from "../../../components/wholesale/ClearedCandidateModal"
import ClearedRunSwitcher from "../../../components/wholesale/ClearedRunSwitcher"
import ClearedValidatorPanel from "../../../components/wholesale/ClearedValidatorPanel"
import DownloadMenu from "../../../components/wholesale/DownloadMenu"
import FileRequirementsPanel from "../../../components/wholesale/FileRequirementsPanel"
import FilterPanel, { TitleStatusGroup } from "../../../components/wholesale/FilterPanel"
import Layout from "../../../components/Layout"
import MatchResultsTable from "../../../components/wholesale/MatchResultsTable"
import {
  WholesaleRunSummary,
  WholesaleTab,
  WholesaleVehicle,
  groupVehicles,
} from "../../../api/wholesale"
import { MOBILE_FULL_BUTTON } from "../../../config/mobileStyles"
import { THEME } from "../../../config/theme"
import { WholesaleProvider, useWholesale } from "../../../context/WholesaleContext"
import { useDealerGroup } from "../../../context/DealerGroupContext"
import { useIsMobile } from "../../../hooks/useIsMobile"
import DealerGroupRequiredNotice from "../../../components/DealerGroupRequiredNotice"

type Mode = "view" | "build"

export default function AdesaClearedListPage() {
  return (
    <Layout>
      <WholesaleProvider>
        <AdesaClearedListPageContent />
      </WholesaleProvider>
    </Layout>
  )
}

function AdesaClearedListPageContent() {
  const {
    session,
    refreshSession,
    deleteFile,
    clearedFamilies,
    clearedFamiliesLoading,
    refreshClearedFamilies,
    runDetail,
    runDetailLoading,
    resumeLatestDraft,
    loadRunDetail,
    createDraft,
    rebucketDraft,
    selectCandidate,
    excludeVehicle,
    restoreVehicle,
    completeRun,
    reopenRun,
    clearRunDetail,
    actionLoading,
    actionError,
    runs,
    refreshRuns,
  } = useWholesale()
  const { dealerGroupId } = useDealerGroup()
  const isMobile = useIsMobile()

  const [mode, setMode] = useState<Mode>("build")
  const [activeTab, setActiveTab] = useState<WholesaleTab>("cleared")
  const [filterExpanded, setFilterExpanded] = useState(true)
  const [pickVehicle, setPickVehicle] = useState<WholesaleVehicle | null>(null)
  const initedRef = useRef(false)
  const creatingRef = useRef(false)
  // Auto-create waits for the cleared-families config so the first draft
  // doesn't bucket with an empty list (everything -> Not Cleared). The gate
  // opens when the families fetch settles, OR after a short safety window so
  // a slow/odd fetch can never hang the upload -> draft flow.
  const [familiesGateOpen, setFamiliesGateOpen] = useState(false)
  const prevFamLoadingRef = useRef(false)

  const run = runDetail?.run ?? null
  const groups = useMemo(() => groupVehicles(runDetail), [runDetail])
  const isDraft = run?.status === "draft"
  const bothUploaded = !!session?.adesa && !!session?.tekion

  // Initial load: session + runs + families + resume the latest draft.
  useEffect(() => {
    refreshSession()
    refreshRuns()
    refreshClearedFamilies()
    resumeLatestDraft()
  }, [refreshSession, refreshRuns, refreshClearedFamilies, resumeLatestDraft])

  // Open the auto-create gate when the families fetch settles (true -> false).
  useEffect(() => {
    if (prevFamLoadingRef.current && !clearedFamiliesLoading) setFamiliesGateOpen(true)
    prevFamLoadingRef.current = clearedFamiliesLoading
  }, [clearedFamiliesLoading])

  // ...or after a safety window, so the upload -> draft flow never hangs even
  // if the families fetch never reports a loading state.
  useEffect(() => {
    const t = window.setTimeout(() => setFamiliesGateOpen(true), 2500)
    return () => window.clearTimeout(t)
  }, [])

  // Once the resume settles, decide the opening mode: a draft -> view it,
  // otherwise drop into the build (upload) state.
  useEffect(() => {
    if (initedRef.current || runDetailLoading) return
    initedRef.current = true
    setMode(runDetail ? "view" : "build")
  }, [runDetailLoading, runDetail])

  // Auto-create the draft once both files are present in build mode (no
  // "Run Report" button). Files are consumed into the draft, then deleted
  // so the panel resets and a later Start New Run requires a fresh upload.
  useEffect(() => {
    if (mode !== "build" || run || actionLoading || creatingRef.current) return
    if (!bothUploaded || dealerGroupId === null) return
    if (!familiesGateOpen) return  // wait for the cleared-families config (bounded)
    creatingRef.current = true
    const adesaId = session?.adesa?.file_id
    const tekionId = session?.tekion?.file_id
    ;(async () => {
      try {
        await createDraft(clearedFamilies.map((f) => f.family), null)
        if (adesaId) await deleteFile(adesaId)
        if (tekionId) await deleteFile(tekionId)
        await refreshRuns()
        setMode("view")
      } catch {
        // actionError surfaces in the header; allow a retry on next change.
      } finally {
        creatingRef.current = false
      }
    })()
  }, [
    mode, run, actionLoading, bothUploaded, dealerGroupId, session,
    clearedFamilies, familiesGateOpen, createDraft, deleteFile, refreshRuns,
  ])

  const titleStatusGroups = useMemo<TitleStatusGroup[]>(
    () => buildTitleStatusGroups(groups?.cleared, groups?.not_cleared),
    [groups],
  )
  const selectedFamilies = useMemo(() => run?.applied_families ?? [], [run])
  const perValue = useMemo(() => run?.applied_per_value ?? {}, [run])

  const handleToggleFamily = useCallback(
    (family: string) => {
      if (!run) return
      const next = selectedFamilies.includes(family)
        ? selectedFamilies.filter((f) => f !== family)
        : [...selectedFamilies, family]
      rebucketDraft(run.id, next, perValue).catch(() => {})
    },
    [run, selectedFamilies, perValue, rebucketDraft],
  )

  const handleTogglePerValue = useCallback(
    (canonical: string, checked: boolean) => {
      if (!run) return
      rebucketDraft(run.id, selectedFamilies, { ...perValue, [canonical]: checked }).catch(() => {})
    },
    [run, selectedFamilies, perValue, rebucketDraft],
  )

  const handleStartNew = useCallback(() => {
    clearRunDetail()
    setMode("build")
    setActiveTab("cleared")
  }, [clearRunDetail])

  const handleSelectRun = useCallback(
    (runId: number) => {
      setMode("view")
      loadRunDetail(runId)
    },
    [loadRunDetail],
  )

  const handlePick = useCallback(
    (tekionStock: string) => {
      if (!run || !pickVehicle?.id) return
      selectCandidate(run.id, pickVehicle.id, tekionStock)
        .then(() => setPickVehicle(null))
        .catch(() => {})
    },
    [run, pickVehicle, selectCandidate],
  )

  // Bulk-approve from the validator: only rows with a *single* candidate are
  // auto-resolved (accept that one Tekion record). Rows with multiple
  // candidates are left unresolved for the user to open and choose. Sequential
  // so each select_candidate is applied in turn; actionError surfaces a
  // failure and the rest continue.
  const handleApproveMany = useCallback(
    async (vehicles: WholesaleVehicle[]) => {
      if (!run) return
      for (const v of vehicles) {
        const candidates = v.match_candidates ?? []
        if (v.id == null || candidates.length !== 1) continue
        const stock = candidates[0].tekion_stock
        if (!stock) continue
        try {
          await selectCandidate(run.id, v.id, stock)
        } catch {
          // surfaced via actionError; keep approving the rest
        }
      }
    },
    [run, selectCandidate],
  )

  // Exclude one or many validator vehicles (keep them out of the cleared list).
  // Sequential so each exclude is applied to the draft in turn.
  const handleExcludeMany = useCallback(
    async (vehicles: WholesaleVehicle[]) => {
      if (!run) return
      for (const v of vehicles) {
        if (v.id == null) continue
        try {
          await excludeVehicle(run.id, v.id)
        } catch {
          // surfaced via actionError; keep going
        }
      }
    },
    [run, excludeVehicle],
  )

  // Exclude the vehicle currently open in the modal, then close it.
  const handleExcludePick = useCallback(() => {
    if (!run || !pickVehicle?.id) return
    excludeVehicle(run.id, pickVehicle.id)
      .then(() => setPickVehicle(null))
      .catch(() => {})
  }, [run, pickVehicle, excludeVehicle])

  // Restore an excluded vehicle back to the validator.
  const handleRestore = useCallback(
    (vehicle: WholesaleVehicle) => {
      if (!run || vehicle.id == null) return
      restoreVehicle(run.id, vehicle.id).catch(() => {})
    },
    [run, restoreVehicle],
  )

  const handleComplete = useCallback(() => {
    if (!run) return
    completeRun(run.id).catch(() => {})
  }, [run, completeRun])

  const handleReopen = useCallback(() => {
    if (!run) return
    reopenRun(run.id).catch(() => {})
  }, [run, reopenRun])

  if (dealerGroupId === null) {
    return (
      <div style={{ padding: THEME.spacing.lg, fontFamily: THEME.fonts.primary }}>
        <DealerGroupRequiredNotice surface="the Adesa cleared list" />
      </div>
    )
  }

  const pendingCount = groups?.needs_selection.length ?? 0
  const canComplete = isDraft && pendingCount === 0

  return (
    <div style={{ padding: isMobile ? THEME.spacing.md : THEME.spacing.lg, fontFamily: THEME.fonts.primary }}>
      <Header
        run={run}
        runs={runs}
        mode={mode}
        isDraft={isDraft}
        canComplete={canComplete}
        pendingCount={pendingCount}
        busy={actionLoading}
        actionError={actionError}
        activeTab={activeTab}
        isMobile={isMobile}
        onSelectRun={handleSelectRun}
        onRefreshRuns={refreshRuns}
        onStartNew={handleStartNew}
        onComplete={handleComplete}
        onReopen={handleReopen}
      />

      {mode === "build" && !run ? (
        <div style={{ display: "flex", flexDirection: "column", gap: THEME.spacing.sm }}>
          <FileRequirementsPanel session={session} />
          <ActiveVendorFilesPanel session={session} />
          {actionLoading && (
            <div style={{ color: THEME.colors.neutral.light.textSecondary, fontSize: 13 }}>
              Starting draft…
            </div>
          )}
        </div>
      ) : runDetailLoading && !run ? (
        <span style={{ color: THEME.colors.neutral.light.textSecondary }}>Loading…</span>
      ) : run ? (
        <>
          {isDraft && (
            <ClearedValidatorPanel
              vehicles={groups?.needs_selection ?? []}
              onPick={setPickVehicle}
              onApprove={handleApproveMany}
              onExclude={handleExcludeMany}
              busy={actionLoading}
            />
          )}
          {isDraft && (
            <div style={{ marginBottom: THEME.spacing.md }}>
              <FilterPanel
                groups={titleStatusGroups}
                selectedFamilies={selectedFamilies}
                perValue={perValue}
                expanded={filterExpanded}
                onToggleExpanded={() => setFilterExpanded((v) => !v)}
                onToggleFamily={handleToggleFamily}
                onTogglePerValue={handleTogglePerValue}
                loading={false}
                error={null}
              />
            </div>
          )}
          <MatchResultsTable
            groups={groups}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            loading={actionLoading}
            editable={isDraft}
            onPickVehicle={setPickVehicle}
            onRestoreVehicle={handleRestore}
          />
        </>
      ) : (
        <div style={{ color: THEME.colors.neutral.light.textSecondary, fontSize: 14, marginTop: THEME.spacing.lg }}>
          No run loaded — click <strong>Start New Run</strong> and upload an Adesa + Tekion file.
        </div>
      )}

      {pickVehicle && (
        <ClearedCandidateModal
          vehicle={pickVehicle}
          loading={actionLoading}
          onSelect={handlePick}
          onExclude={handleExcludePick}
          onClose={() => setPickVehicle(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

interface HeaderProps {
  run: WholesaleRunSummary | null
  runs: WholesaleRunSummary[]
  mode: Mode
  isDraft: boolean
  canComplete: boolean
  pendingCount: number
  busy: boolean
  actionError: string | null
  activeTab: WholesaleTab
  isMobile: boolean
  onSelectRun: (runId: number) => void
  onRefreshRuns: () => void
  onStartNew: () => void
  onComplete: () => void
  onReopen: () => void
}

function Header({
  run,
  runs,
  mode,
  isDraft,
  canComplete,
  pendingCount,
  busy,
  actionError,
  activeTab,
  isMobile,
  onSelectRun,
  onRefreshRuns,
  onStartNew,
  onComplete,
  onReopen,
}: HeaderProps) {
  const subtitle = buildSubtitle(run, mode, pendingCount)
  const mobileBtn = isMobile ? MOBILE_FULL_BUTTON : {}

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? THEME.spacing.sm : 0,
        marginBottom: THEME.spacing.md,
      }}
    >
      <div>
        <h1
          style={{
            fontSize: isMobile ? 20 : 24,
            fontWeight: 500,
            letterSpacing: "-0.5px",
            margin: 0,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          Adesa Cleared List
        </h1>
        {subtitle && (
          <div style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary, marginTop: 4 }}>
            {subtitle}
          </div>
        )}
        {/* MOBILE: the run switcher drops below the title as a full-width row;
            DESKTOP: it lives in the right-hand controls cluster instead. */}
        {isMobile && (
          <div style={{ marginTop: THEME.spacing.sm }}>
            <ClearedRunSwitcher runs={runs} current={run} onSelect={onSelectRun} onOpen={onRefreshRuns} fullWidth />
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: isMobile ? "wrap" : "nowrap",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? THEME.spacing.sm : 12,
        }}
      >
        {!isMobile && (
          <ClearedRunSwitcher runs={runs} current={run} onSelect={onSelectRun} onOpen={onRefreshRuns} />
        )}
        {actionError && (
          <span style={{ color: THEME.colors.semantic.danger, fontSize: 13, ...(isMobile && { flexBasis: "100%" }) }}>
            {actionError}
          </span>
        )}
        {run && run.status === "completed" && (
          <button type="button" onClick={onReopen} disabled={busy} style={{ ...secondaryButton(!busy), ...mobileBtn }}>
            Reopen
          </button>
        )}
        {run && (
          <button type="button" onClick={onStartNew} style={{ ...(run.status === "completed" ? brandPrimaryButton(true) : secondaryButton(true)), ...mobileBtn }}>
            Start New Run
          </button>
        )}
        {mode === "build" && !run && busy && (
          <button type="button" style={{ ...brandPrimaryButton(false), ...mobileBtn }} disabled>
            Starting draft…
          </button>
        )}
        {isDraft && (
          <button
            type="button"
            onClick={onComplete}
            disabled={!canComplete || busy}
            title={!canComplete ? `${pendingCount} vehicle(s) still need a selection` : undefined}
            style={{ ...brandPrimaryButton(canComplete && !busy), ...mobileBtn }}
          >
            {busy ? "Working…" : "Mark Completed"}
          </button>
        )}
        {isMobile ? (
          <div style={{ flexBasis: "100%", display: "flex" }}>
            <DownloadMenu
              runId={run && run.status === "completed" ? run.id : null}
              activeTab={activeTab}
              runStartedAt={run?.started_at ?? null}
              fullWidth
            />
          </div>
        ) : (
          <DownloadMenu
            runId={run && run.status === "completed" ? run.id : null}
            activeTab={activeTab}
            runStartedAt={run?.started_at ?? null}
          />
        )}
      </div>
    </div>
  )
}

function buildSubtitle(run: WholesaleRunSummary | null, mode: Mode, pendingCount: number): string {
  if (!run) {
    return mode === "build" ? "Upload an Adesa + Tekion file to start a draft" : ""
  }
  const when = formatDateTime(run.started_at)
  const status = run.status === "draft" ? "Draft" : "Completed (read-only)"
  const counts = `${run.cleared_count} cleared, ${run.not_cleared_count} not cleared, ${run.unmatched_adesa_count} unmatched`
  const pending = pendingCount > 0 ? ` · ${pendingCount} need a pick` : ""
  return `${when} · ${status} · ${counts}${pending}`
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

// Build the Title Status families hierarchy for the filter panel from the
// matched (cleared + not_cleared) vehicles' Tekion Title Status.
function buildTitleStatusGroups(
  cleared: WholesaleVehicle[] | undefined,
  notCleared: WholesaleVehicle[] | undefined,
): TitleStatusGroup[] {
  const matched = [...(cleared ?? []), ...(notCleared ?? [])]
  const byCanonical = new Map<string, { family: string; count: number }>()
  for (const vehicle of matched) {
    const canonical = canonicalizeTitleStatus(vehicle.tekion_title_status)
    if (!canonical) continue
    const family = canonical.split(" ")[0]
    const entry = byCanonical.get(canonical) ?? { family, count: 0 }
    entry.count += 1
    byCanonical.set(canonical, entry)
  }
  const byFamily = new Map<string, TitleStatusGroup>()
  byCanonical.forEach((entry, canonical) => {
    let group = byFamily.get(entry.family)
    if (!group) {
      group = { family: entry.family, totalCount: 0, values: [] }
      byFamily.set(entry.family, group)
    }
    group.values.push({ canonical, count: entry.count })
    group.totalCount += entry.count
  })
  const result = Array.from(byFamily.values())
  result.sort((a, b) => a.family.localeCompare(b.family))
  for (const group of result) group.values.sort((a, b) => b.count - a.count)
  return result
}

function canonicalizeTitleStatus(raw: string | null): string {
  if (!raw) return ""
  return raw.split(/\s+/).filter((t) => t !== "").join(" ").toUpperCase()
}

function brandPrimaryButton(enabled: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 14,
    fontWeight: 500,
    color: THEME.colors.white,
    backgroundColor: THEME.colors.navy,
    border: "none",
    borderRadius: THEME.borderRadius.md,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    userSelect: "none",
    transition: "opacity 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: THEME.spacing.xs,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}

function secondaryButton(enabled: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 14,
    fontWeight: 500,
    background: THEME.colors.neutral.light.surface,
    color: THEME.colors.neutral.light.textPrimary,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    userSelect: "none",
    transition: "opacity 0.15s",
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}
